import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, Stack, TextField, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TablePagination,
  InputAdornment, CircularProgress, Avatar
} from "@mui/material";
import { Add, Edit, Delete, Search, CloudUpload, RequestQuote, GetApp } from "@mui/icons-material";
import { 
  collection, query, where, getDocs, addDoc, updateDoc, 
  deleteDoc, doc, Timestamp, orderBy, getDoc 
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "../../firebase";
import Swal from "sweetalert2";

function Income() {
  const [incomeList, setIncomeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [schoolId, setSchoolId] = useState(null);
  
  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Dialog State
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    amount: "", 
    date: new Date().toISOString().split('T')[0],
    description: "", 
    personName: "", // Payer Name
    imageUrl: ""
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { getInitialData(); }, []);

  const getInitialData = async () => {
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const sId = userSnap.data()?.schoolId;
      setSchoolId(sId);
      if (sId) fetchIncome(sId);
    } catch (e) { console.error(e); }
  };

  const fetchIncome = async (sId) => {
    setLoading(true);
    try {
      const q = query(collection(db, "income"), where("schoolId", "==", sId), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setIncomeList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleFileUpload = async () => {
    if (!file) return formData.imageUrl || "";
    const storageRef = ref(storage, `income/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleSubmit = async () => {
    if (!formData.amount || !formData.personName) {
      return Swal.fire("Error", "Payer Name and Amount are required", "error");
    }
    
    setSubmitting(true);
    try {
      const imageUrl = await handleFileUpload();
      const payload = {
        ...formData,
        amount: formData.amount.toString(),
        imageUrl,
        schoolId,
        updatedAt: Timestamp.now(),
        createdAt: editId ? formData.createdAt : Timestamp.now()
      };

      if (editId) {
        await updateDoc(doc(db, "income", editId), payload);
      } else {
        await addDoc(collection(db, "income"), payload);
      }

      Swal.fire("Success", "Income record saved", "success");
      setOpen(false);
      resetForm();
      fetchIncome(schoolId);
    } catch (e) { 
      Swal.fire("Error", e.message, "error"); 
    } finally { 
      setSubmitting(false); 
    }
  };

  const handleDelete = async (id) => {
    const res = await Swal.fire({ 
      title: 'Delete Income Record?', 
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true, 
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, Delete' 
    });
    
    if (res.isConfirmed) {
      await deleteDoc(doc(db, "income", id));
      fetchIncome(schoolId);
      Swal.fire("Deleted", "Record has been removed.", "success");
    }
  };

  const resetForm = () => {
    setFormData({ amount: "", date: new Date().toISOString().split('T')[0], description: "", personName: "", imageUrl: "" });
    setEditId(null);
    setFile(null);
  };

  const filteredIncome = incomeList.filter(item => 
    item.personName?.toLowerCase().includes(search.toLowerCase()) || 
    item.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box p={1} bgcolor="#f8fafc" minHeight="100vh">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="primary.dark">Income Ledger</Typography>
          <Typography variant="body2" color="text.secondary">Manage non-fee revenue sources</Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={() => { resetForm(); setOpen(true); }}
          sx={{ borderRadius: 2, px: 3 }}
        >
          Add Income
        </Button>
      </Stack>

      <Card sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
        <TextField 
          fullWidth 
          placeholder="Search by payer or source..." 
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ 
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
            sx: { borderRadius: 2 }
          }}
        />
      </Card>

      <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: "#eff6ff" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Sr No</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Proof</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Received From</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={30} sx={{ my: 3 }}/></TableCell></TableRow>
            ) : filteredIncome.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>No records found.</TableCell></TableRow>
            ) : (
              filteredIncome.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => (
                <TableRow key={item.id} hover>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>
                    {item.imageUrl ? (
                      <Avatar 
                        src={item.imageUrl} 
                        variant="rounded" 
                        sx={{ cursor: 'pointer', width: 40, height: 40 }} 
                        onClick={() => window.open(item.imageUrl)}
                      />
                    ) : (
                      <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: '#f1f5f9' }}>
                        <RequestQuote sx={{ color: '#94a3b8' }}/>
                      </Avatar>
                    )}
                  </TableCell>
                  <TableCell>{item.date}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{item.personName}</TableCell>
                  <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.description}
                  </TableCell>
                  <TableCell sx={{ color: 'success.main', fontWeight: 700 }}>₹{item.amount}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton onClick={() => { setEditId(item.id); setFormData(item); setOpen(true); }} size="small" color="primary">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(item.id)} size="small" color="error">
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={filteredIncome.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>

      {/* Add/Edit Income Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>{editId ? "Edit Income Entry" : "New Income Entry"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} mt={1}>
            <TextField 
              label="Received From (Person/Source)" 
              fullWidth 
              value={formData.personName} 
              onChange={e => setFormData({...formData, personName: e.target.value})}
            />
            <TextField 
              label="Amount" 
              type="number" 
              fullWidth 
              value={formData.amount} 
              onChange={e => setFormData({...formData, amount: e.target.value})}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            />
            <TextField 
              label="Date" 
              type="date" 
              fullWidth 
              value={formData.date} 
              onChange={e => setFormData({...formData, date: e.target.value})} 
              InputLabelProps={{ shrink: true }}
            />
            <TextField 
              label="Description / Remarks" 
              multiline 
              rows={3} 
              fullWidth 
              value={formData.description} 
              onChange={e => setFormData({...formData, description: e.target.value})}
            />
            
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>ATTACHMENT (RECEIPT OR CHECK PHOTO)</Typography>
              <Button 
                variant="outlined" 
                component="label" 
                fullWidth 
                startIcon={<CloudUpload />}
                sx={{ borderStyle: 'dashed', py: 1.5, mt: 0.5 }}
              >
                {file ? file.name : (formData.imageUrl ? "Change Attached Photo" : "Upload File")}
                <input type="file" hidden onChange={e => setFile(e.target.files[0])} accept="image/*" />
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={submitting}
            sx={{ minWidth: 120 }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : "Save Record"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Income;